import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { tap } from 'rxjs';

import { NotificationService } from '@core/services/notification.service';
import { AgentsActions } from '@stores/agents/agents.actions';
import { AiModelsActions } from '@stores/ai-models/ai-models.actions';
import { AiProvidersActions } from '@stores/ai-providers/ai-providers.actions';
import { BusinessEntitiesActions } from '@stores/business-entities/business-entities.actions';
import { PayloadsActions } from '@stores/payloads/payloads.actions';
import { RbacActions } from '@stores/rbac/rbac.actions';
import { TenantsActions } from '@stores/tenants/tenants.actions';

const SUCCESS_MESSAGES: Record<string, string> = {
  // Tenants
  [TenantsActions.createTenantSuccess.type]: 'Tenant created.',
  [TenantsActions.updateTenantSuccess.type]: 'Tenant updated.',
  [TenantsActions.deleteTenantSuccess.type]: 'Tenant deleted.',
  [TenantsActions.createSubscriptionSuccess.type]: 'Subscription created.',
  // Groups
  [RbacActions.createGroupSuccess.type]: 'Group created.',
  [RbacActions.updateGroupSuccess.type]: 'Group updated.',
  [RbacActions.deleteGroupSuccess.type]: 'Group deleted.',
  [RbacActions.assignGroupMemberSuccess.type]: 'Member added to group.',
  [RbacActions.removeGroupMemberSuccess.type]: 'Member removed from group.',
  // Accounts
  [RbacActions.createAccountSuccess.type]: 'Account created.',
  [RbacActions.updateAccountSuccess.type]: 'Account updated.',
  [RbacActions.deleteAccountSuccess.type]: 'Account deleted.',
  [RbacActions.resetAccountPasswordSuccess.type]: 'Password reset email sent.',
  [RbacActions.blockAccountSuccess.type]: 'Account blocked.',
  [RbacActions.unblockAccountSuccess.type]: 'Account unblocked.',
  // Permissions
  [RbacActions.grantPermissionSuccess.type]: 'Permission granted.',
  [RbacActions.revokePermissionSuccess.type]: 'Permission revoked.',
  // Invitations
  [RbacActions.sendInvitationSuccess.type]: 'Invitation sent.',
  [RbacActions.revokeInvitationSuccess.type]: 'Invitation revoked.',
  // Agents
  [AgentsActions.createAgentSuccess.type]: 'Agent created.',
  [AgentsActions.updateAgentSuccess.type]: 'Agent updated.',
  [AgentsActions.deleteAgentSuccess.type]: 'Agent deleted.',
  [AgentsActions.updateAgentEverythingSuccess.type]: 'Agent saved.',
  [AgentsActions.createInstructionSuccess.type]: 'Instruction created.',
  [AgentsActions.updateInstructionSuccess.type]: 'Instruction updated.',
  [AgentsActions.deleteInstructionSuccess.type]: 'Instruction deleted.',
  [AgentsActions.createPromptSuccess.type]: 'Prompt created.',
  [AgentsActions.updatePromptSuccess.type]: 'Prompt updated.',
  [AgentsActions.deletePromptSuccess.type]: 'Prompt deleted.',
  [AgentsActions.createAgentInputSuccess.type]: 'Input created.',
  [AgentsActions.updateAgentInputSuccess.type]: 'Input updated.',
  [AgentsActions.deleteAgentInputSuccess.type]: 'Input deleted.',
  [AgentsActions.createAgentVersionSuccess.type]: 'Agent version created.',
  [AgentsActions.updateAgentVersionStatusSuccess.type]: 'Version status updated.',
};

const ERROR_FALLBACKS: Record<string, string> = {
  // Tenants
  [TenantsActions.loadFailure.type]: 'Failed to load tenants.',
  [TenantsActions.createTenantFailure.type]: 'Failed to create tenant.',
  [TenantsActions.updateTenantFailure.type]: 'Failed to update tenant.',
  [TenantsActions.deleteTenantFailure.type]: 'Failed to delete tenant.',
  [TenantsActions.createSubscriptionFailure.type]: 'Failed to create subscription.',
  // Groups
  [RbacActions.loadGroupsFailure.type]: 'Failed to load groups.',
  [RbacActions.createGroupFailure.type]: 'Failed to create group.',
  [RbacActions.updateGroupFailure.type]: 'Failed to update group.',
  [RbacActions.deleteGroupFailure.type]: 'Failed to delete group.',
  [RbacActions.assignGroupMemberFailure.type]: 'Failed to assign member.',
  [RbacActions.removeGroupMemberFailure.type]: 'Failed to remove member.',
  // Accounts
  [RbacActions.loadAccountsFailure.type]: 'Failed to load accounts.',
  [RbacActions.createAccountFailure.type]: 'Failed to create account.',
  [RbacActions.updateAccountFailure.type]: 'Failed to update account.',
  [RbacActions.deleteAccountFailure.type]: 'Failed to delete account.',
  [RbacActions.resetAccountPasswordFailure.type]: 'Failed to send password reset.',
  [RbacActions.blockAccountFailure.type]: 'Failed to block account.',
  [RbacActions.unblockAccountFailure.type]: 'Failed to unblock account.',
  // Permissions
  [RbacActions.loadFeatureDetailsFailure.type]: 'Failed to load feature details.',
  [RbacActions.loadPermissionsFailure.type]: 'Failed to load permissions.',
  [RbacActions.grantPermissionFailure.type]: 'Failed to grant permission.',
  [RbacActions.revokePermissionFailure.type]: 'Failed to revoke permission.',
  // Invitations
  [RbacActions.loadInvitationsFailure.type]: 'Failed to load invitations.',
  [RbacActions.sendInvitationFailure.type]: 'Failed to send invitation.',
  [RbacActions.revokeInvitationFailure.type]: 'Failed to revoke invitation.',
  // AI Models
  [AiModelsActions.loadFailure.type]: 'Failed to load AI models.',
  // AI Providers
  [AiProvidersActions.loadFailure.type]: 'Failed to load AI providers.',
  // Business Entities
  [BusinessEntitiesActions.loadFailure.type]: 'Failed to load business entities.',
  // Payloads
  [PayloadsActions.getPayloadsFailure.type]: 'Failed to load payloads.',
  // Agents — load
  [AgentsActions.loadFailure.type]: 'Failed to load agents.',
  [AgentsActions.getAgentHubsFailure.type]: 'Failed to load agent hubs.',
  [AgentsActions.getAgentFailure.type]: 'Failed to load agent details.',
  [AgentsActions.getInstructionFailure.type]: 'Failed to load instructions.',
  [AgentsActions.getPromptsFailure.type]: 'Failed to load prompts.',
  [AgentsActions.getAgentInputsFailure.type]: 'Failed to load agent inputs.',
  [AgentsActions.getAgentVersionsFailure.type]: 'Failed to load agent versions.',
  // Agents — mutations
  [AgentsActions.createAgentFailure.type]: 'Failed to create agent.',
  [AgentsActions.updateAgentFailure.type]: 'Failed to update agent.',
  [AgentsActions.deleteAgentFailure.type]: 'Failed to delete agent.',
  [AgentsActions.createInstructionFailure.type]: 'Failed to create instruction.',
  [AgentsActions.updateInstructionFailure.type]: 'Failed to update instruction.',
  [AgentsActions.deleteInstructionFailure.type]: 'Failed to delete instruction.',
  [AgentsActions.createPromptFailure.type]: 'Failed to create prompt.',
  [AgentsActions.updatePromptFailure.type]: 'Failed to update prompt.',
  [AgentsActions.deletePromptFailure.type]: 'Failed to delete prompt.',
  [AgentsActions.createAgentInputFailure.type]: 'Failed to create input.',
  [AgentsActions.updateAgentInputFailure.type]: 'Failed to update input.',
  [AgentsActions.deleteAgentInputFailure.type]: 'Failed to delete input.',
  [AgentsActions.createAgentVersionFailure.type]: 'Failed to create agent version.',
  [AgentsActions.updateAgentEverythingFailure.type]: 'Failed to save agent.',
};

@Injectable()
export class NotificationEffects {
  private readonly actions$ = inject(Actions);
  private readonly notificationSvc = inject(NotificationService);

  success$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TenantsActions.createTenantSuccess,
          TenantsActions.updateTenantSuccess,
          TenantsActions.deleteTenantSuccess,
          TenantsActions.createSubscriptionSuccess,
          RbacActions.createGroupSuccess,
          RbacActions.updateGroupSuccess,
          RbacActions.deleteGroupSuccess,
          RbacActions.assignGroupMemberSuccess,
          RbacActions.removeGroupMemberSuccess,
          RbacActions.createAccountSuccess,
          RbacActions.updateAccountSuccess,
          RbacActions.deleteAccountSuccess,
          RbacActions.resetAccountPasswordSuccess,
          RbacActions.blockAccountSuccess,
          RbacActions.unblockAccountSuccess,
          RbacActions.grantPermissionSuccess,
          RbacActions.revokePermissionSuccess,
          RbacActions.sendInvitationSuccess,
          RbacActions.revokeInvitationSuccess,
          AgentsActions.createAgentSuccess,
          AgentsActions.updateAgentSuccess,
          AgentsActions.deleteAgentSuccess,
          AgentsActions.updateAgentEverythingSuccess,
          AgentsActions.createInstructionSuccess,
          AgentsActions.updateInstructionSuccess,
          AgentsActions.deleteInstructionSuccess,
          AgentsActions.createPromptSuccess,
          AgentsActions.updatePromptSuccess,
          AgentsActions.deletePromptSuccess,
          AgentsActions.createAgentInputSuccess,
          AgentsActions.updateAgentInputSuccess,
          AgentsActions.deleteAgentInputSuccess,
          AgentsActions.createAgentVersionSuccess,
          AgentsActions.updateAgentVersionStatusSuccess,
        ),
        tap((action) => this.notificationSvc.success(SUCCESS_MESSAGES[action.type] ?? 'Done.')),
      ),
    { dispatch: false },
  );

  failure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TenantsActions.loadFailure,
          TenantsActions.createTenantFailure,
          TenantsActions.updateTenantFailure,
          TenantsActions.deleteTenantFailure,
          TenantsActions.createSubscriptionFailure,
          RbacActions.loadGroupsFailure,
          RbacActions.createGroupFailure,
          RbacActions.updateGroupFailure,
          RbacActions.deleteGroupFailure,
          RbacActions.assignGroupMemberFailure,
          RbacActions.removeGroupMemberFailure,
          RbacActions.loadAccountsFailure,
          RbacActions.createAccountFailure,
          RbacActions.updateAccountFailure,
          RbacActions.deleteAccountFailure,
          RbacActions.resetAccountPasswordFailure,
          RbacActions.blockAccountFailure,
          RbacActions.unblockAccountFailure,
          RbacActions.loadFeatureDetailsFailure,
          RbacActions.loadPermissionsFailure,
          RbacActions.grantPermissionFailure,
          RbacActions.revokePermissionFailure,
          RbacActions.loadInvitationsFailure,
          RbacActions.sendInvitationFailure,
          RbacActions.revokeInvitationFailure,
          AiModelsActions.loadFailure,
          AiProvidersActions.loadFailure,
          BusinessEntitiesActions.loadFailure,
          PayloadsActions.getPayloadsFailure,
          AgentsActions.loadFailure,
          AgentsActions.getAgentHubsFailure,
          AgentsActions.getAgentFailure,
          AgentsActions.getInstructionFailure,
          AgentsActions.getPromptsFailure,
          AgentsActions.getAgentInputsFailure,
          AgentsActions.getAgentVersionsFailure,
          AgentsActions.createAgentFailure,
          AgentsActions.updateAgentFailure,
          AgentsActions.deleteAgentFailure,
          AgentsActions.createInstructionFailure,
          AgentsActions.updateInstructionFailure,
          AgentsActions.deleteInstructionFailure,
          AgentsActions.createPromptFailure,
          AgentsActions.updatePromptFailure,
          AgentsActions.deletePromptFailure,
          AgentsActions.createAgentInputFailure,
          AgentsActions.updateAgentInputFailure,
          AgentsActions.deleteAgentInputFailure,
          AgentsActions.createAgentVersionFailure,
          AgentsActions.updateAgentEverythingFailure,
        ),
        tap((action) => {
          const fallback = ERROR_FALLBACKS[action.type] ?? 'An unexpected error occurred.';
          const msg = (action as any).error?.apiError?.message || fallback;
          this.notificationSvc.error(msg);
        }),
      ),
    { dispatch: false },
  );
}
